import type { GetDomainOptions } from "./domains"
import { getDomain } from "./domains"
import { config } from "../../config"

type GetBaseUrlOptions = GetDomainOptions & {
  protocol?: string
  port?: number
}

export type GetUrlOptions = GetBaseUrlOptions & {
  path: string
  currentLanguage: string
  absolute?: boolean
}

/**
 * Returns the base URL for the given language with proper protocol and port configuration.
 *
 * @returns Object containing the constructed base URL and whether this is the default language
 * @example
 * getBaseUrl({ language: "en", defaultDomain: "localhost", enableI18nDomains: true })
 * // Returns: { baseUrl: "https://bounce.com", isDefaultLanguage: true }
 *
 * getBaseUrl({ language: "zh", defaultDomain: "localhost", enableI18nDomains: true })
 * // Returns: { baseUrl: "https://zh.bounce.com", isDefaultLanguage: true }
 *
 * getBaseUrl({ language: "zh", defaultDomain: "localhost", enableI18nDomains: false })
 * // Returns: { baseUrl: "http://localhost:3002", isDefaultLanguage: false }
 */
export const getBaseUrl = ({
  language,
  defaultDomain,
  enableI18nDomains,
  port = config.port,
  protocol = config.protocol,
}: GetBaseUrlOptions) => {
  const { domain, isDefaultLanguage } = getDomain({ language, defaultDomain, enableI18nDomains })

  // add the protocol and port
  let baseUrl = `${protocol}://${domain}`
  baseUrl = port ? `${baseUrl}:${port}` : baseUrl

  return { baseUrl, isDefaultLanguage }
}

/**
 * Remove url params and trailing slash
 * @param path - The path to get the canonical path for.
 * @returns The canonical path.
 * @example
 * getCanonicalPath("/city/nice") // "/city/nice"
 * getCanonicalPath("/city/nice?param=value") // "/city/nice"
 * getCanonicalPath("/") // ""
 */
export const getCanonicalPath = (path: string) => {
  let canonicalPath = path.split("?")[0]
  canonicalPath = canonicalPath !== "/" ? canonicalPath : ""
  return canonicalPath ?? ""
}

/**
 * Check if the domain of 2 languages are the sampleRate
 * @example
 * isSameDomain({language: "en", currentLanguage: "zh"}) // false
 * isSameDomain({language: "en", currentLanguage: "de"}) // true
 */
const isSameDomain = ({ language, currentLanguage, ...domainOptions }: Omit<GetUrlOptions, "path" | "absolute">) => {
  if (language === currentLanguage) return true

  const { domain } = getDomain({ language, ...domainOptions })
  const { domain: currentDomain } = getDomain({ language: currentLanguage, ...domainOptions })
  return currentDomain === domain
}

/**
 * Generates a URL for a specific language and path, with support for both absolute and relative URLs.
 *
 * This function handles internationalization (i18n) URL generation with the following logic:
 * - For i18n domains: uses language-specific subdomains (e.g., zh.bounce.com)
 * - For non-i18n domains: uses language prefixes in the path (e.g., /de/path)
 * - Supports both absolute URLs (with protocol and domain) and relative URLs
 * - Optimizes for same-domain navigation by returning relative URLs when appropriate
 *
 * @example
 * // Absolute URL for Chinese with i18n domains enabled
 * getCanonicalUrl({ path: "/city/paris", language: "zh", currentLanguage: "en", enableI18nDomains: true })
 * // Returns: { url: "https://zh.bounce.com/city/paris", isDefaultLanguage: true }
 *
 * // Relative URL for German when on same domain
 * getCanonicalUrl({ path: "/city/paris", language: "de", currentLanguage: "en", absolute: false, enableI18nDomains: true })
 * // Returns: { url: "/de/city/paris", isDefaultLanguage: false }
 */
export const getCanonicalUrl = ({
  path,
  language,
  currentLanguage,
  absolute = true,
  ...baseUrlsOptions
}: GetUrlOptions) => {
  const { baseUrl, isDefaultLanguage } = getBaseUrl({ language, ...baseUrlsOptions })
  const languagePrefix = isDefaultLanguage ? "" : `/${language}`
  const canonicalPath = getCanonicalPath(path)
  const sameDomain = isSameDomain({ language, currentLanguage, ...baseUrlsOptions })

  const basePath = `${languagePrefix}${canonicalPath}`
  let url = `${baseUrl}${basePath}`

  // Optimization: Use relative path if URL points to the same domain and absolute is false
  if (!absolute && sameDomain) {
    return {
      url: basePath.trim() || "/",
      isDefaultLanguage,
    }
  }

  return {
    url,
    isDefaultLanguage,
  }
}
